import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { Table, type TableSort } from 'src/components/TableNew';
import PageHeader from 'src/components/PageHeader';
import NoResult from 'src/components/NoResult/NoResult';
import PagePanel from 'src/components/PagePanel';
import { FilterBar, type FilterDef } from 'src/components/FilterBar';
import { useFilterQueryEffect } from 'src/hooks/useFilterQueryEffect';
import { useResetPageParam } from 'src/hooks/useResetPageParam';
import { useDispatch } from 'src/store/useDispatch';
import { convertDate } from 'src/components/Table/helpers';
import { formatCaseUserName } from 'src/api/adminCompliance';
import { dateInputToIso } from 'src/api/adminLogs';
import { ROUTE_USERS } from 'src/utils/routes';
import {
  ORDERS_HEADERS,
  ORDER_DIRECTION_OPTIONS,
  ORDER_KIND_OPTIONS,
  ORDER_STATUS_OPTIONS,
} from 'src/utils/constants';
import { Option, fetchPairOptions } from 'src/api/reference';
import { fetchOrders } from 'src/store/slice/orders';
import { UserLink } from 'src/pages/Wallets/styled';

import OrderDetailModal from './OrderDetailModal';

// One bar per filter; the API ANDs them. `kind` rides on the endpoint's
// field/value search (the slice does that mapping); the dates are turned into
// ISO instants at query time.
const ORDER_FILTERS: FilterDef[] = [
  { key: 'status', label: 'Status', options: ORDER_STATUS_OPTIONS, allLabel: 'All statuses' },
  { key: 'direction', label: 'Direction', options: ORDER_DIRECTION_OPTIONS, allLabel: 'Buy & sell' },
  { key: 'kind', label: 'Kind', options: ORDER_KIND_OPTIONS, allLabel: 'All kinds' },
  { key: 'pair', label: 'Pair', options: [], allLabel: 'All pairs' },
  { key: 'from', label: 'From', type: 'date' },
  { key: 'to', label: 'To', type: 'date' },
];

// Columns the API can order by, mapped from the table's field name. Direction,
// pair and kind are deliberately absent: no index serves them, and the endpoint
// rejects them rather than sort a paged list in memory.
const SORT_FIELDS: Record<string, string> = {
  status: 'status',
  created_display: 'created_at',
};

const Orders = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selected, setSelected] = useState<Record<string, any> | null>(null);
  // Pairs are data rather than an enum, so they come from the API.
  const [pairOptions, setPairOptions] = useState<Option[]>([]);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sort, setSort] = useState<TableSort>(null);
  const navigate = useNavigate();
  const resetPageParam = useResetPageParam();

  useEffect(() => {
    let active = true;
    fetchPairOptions().then((options) => {
      if (active) setPairOptions(options);
    });
    return () => {
      active = false;
    };
  }, []);

  const dispatch = useDispatch();
  const {
    data: { orders, totalCount, totalPages },
    loading,
  } = useSelector(({ orders }) => orders);

  const orderFilters = useMemo(
    () =>
      ORDER_FILTERS.map((filter) =>
        filter.key === 'pair' ? { ...filter, options: pairOptions } : filter,
      ),
    [pairOptions],
  );

  const runQuery = useCallback(
    (params: { page: number; filters: Record<string, string> }) => {
      const { from, to, ...rest } = params.filters;
      dispatch(
        fetchOrders({
          page: params.page,
          ...rest,
          // `to` is exclusive on the API, so the picked day is included by
          // asking for the end of it rather than midnight.
          from: dateInputToIso(from ?? ''),
          to: dateInputToIso(to ?? '', true),
          sort: sort ? SORT_FIELDS[sort.field] : undefined,
          order: sort?.direction,
        }),
      );
    },
    // `sort` belongs to the query, so a change re-runs it from page 1.
    [dispatch, sort],
  );

  const { applied: appliedFilters, refresh: refreshQuery } = useFilterQueryEffect({
    filters,
    currentPage,
    setCurrentPage,
    query: runQuery,
  });

  // Pagination reads the page from the URL, so a new query has to clear it
  // there too or the old page comes straight back. Read through a ref: the
  // callback's identity follows the URL, and depending on it directly would
  // undo every page turn.
  const resetPageParamRef = useRef(resetPageParam);
  resetPageParamRef.current = resetPageParam;
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    resetPageParamRef.current();
  }, [appliedFilters, sort]);

  const rows = useMemo(
    () =>
      (orders ?? []).map((order: Record<string, any>) => {
        const created = order.created_at ? convertDate(order.created_at) : null;
        return {
          ...order,
          user_name: formatCaseUserName(order.owner),
          created_display: created ? `${created.date} ${created.time}` : '—',
        };
      }),
    [orders],
  );

  return (
    <PagePanel>
      <PageHeader title={`Orders (${totalCount})`} hideSearch />

      <FilterBar
        filters={orderFilters}
        values={filters}
        onChange={(key, value) => setFilters((prev) => ({ ...prev, [key]: value }))}
        onClear={() => setFilters({})}
        onRefresh={refreshQuery}
      />

      {rows.length > 0 || loading ? (
        <Table
          loading={loading}
          headers={ORDERS_HEADERS}
          items={rows}
          columns="1.3fr 0.8fr 0.9fr 0.8fr 0.9fr 1.1fr"
          fieldsToShow={['user_name', 'direction', 'pair', 'kind', 'status', 'created_display']}
          sortableFields={Object.keys(SORT_FIELDS)}
          sort={sort}
          onSortChange={setSort}
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          disableRowDetails
          onRowClick={(item) => setSelected(item)}
          renderCell={(item, fieldName) => {
            if (fieldName === 'user_name') {
              const value = String(item.user_name || '—');
              const ownerId = item.owner?.id;
              if (!ownerId) return <span>{value}</span>;
              return (
                <UserLink
                  type="button"
                  title={item.owner?.email ? `${value} (${item.owner.email})` : value}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`${ROUTE_USERS}/${ownerId}`);
                  }}
                >
                  {value}
                </UserLink>
              );
            }
            return null;
          }}
        />
      ) : (
        <NoResult />
      )}

      {selected && (
        <OrderDetailModal order={selected} onClose={() => setSelected(null)} />
      )}
    </PagePanel>
  );
};

export default Orders;
